/**
 * Definiciones de misiones en datos. El motor de misiones del servidor interpreta cada tipo de
 * objetivo de forma genérica, de modo que una misión nueva solo requiere una definición nueva
 * (y, si usa un tipo de objetivo nuevo, un manejador nuevo).
 */

export type RandomEventKind = 'blackout' | 'wind' | 'lowGravity' | 'confetti';

export type ObjectiveDef =
  | {
      type: 'deliver';
      id: string;
      title: string;
      hint: string;
      /** IDs de interactuables 'cellSpawn' donde aparecen los objetos. */
      spawnIds: string[];
      /** Interactuable de destino ('socket'). */
      targetId: string;
    }
  | {
      type: 'simultaneous';
      id: string;
      title: string;
      hint: string;
      plateIds: string[];
      holdSeconds: number;
    }
  | {
      type: 'codeMatch';
      id: string;
      title: string;
      hint: string;
      displayId: string;
      valveIds: string[];
      colorCount: number;
    }
  | {
      type: 'holdZone';
      id: string;
      title: string;
      hint: string;
      /** Interactuable que inicia la fase (palanca). */
      triggerId: string;
      zoneId: string;
      holdSeconds: number;
      /** Fracción de jugadores conectados necesaria dentro de la zona (mínimo 1). */
      fraction: number;
    };

export type ObjectiveType = ObjectiveDef['type'];

export interface MissionDef {
  id: string;
  name: string;
  description: string;
  mapId: string;
  timeLimitSec: number;
  minPlayers: number;
  objectives: ObjectiveDef[];
  rewards: { successScore: number; failScore: number };
  randomEvents: { pool: RandomEventKind[]; minIntervalSec: number; maxIntervalSec: number; durationSec: number };
  /** Reglas de recuperación documentadas y aplicadas por el motor. */
  recovery: {
    /** Segundos tras los que un objeto caído fuera del mapa o abandonado vuelve a su origen. */
    itemRespawnSec: number;
    /** Los objetos llevados por un jugador que se desconecta se sueltan en su posición. */
    dropOnDisconnect: true;
  };
}

export const MISSIONS: Record<string, MissionDef> = {
  'reactivate-city': {
    id: 'reactivate-city',
    name: 'Reactivar la ciudad',
    description:
      'Kokun City se ha quedado sin energía (alguien enchufó 400 tostadoras). Recuperad las células, sincronizad las placas, calibrad el generador y arrancadlo antes de que se acabe el tiempo.',
    mapId: 'kokun-city',
    timeLimitSec: 8 * 60,
    minPlayers: 1,
    objectives: [
      {
        type: 'deliver',
        id: 'cells',
        title: 'Entrega las 3 células de energía',
        hint: 'Una está en la azotea de la pizzería, otra en un patio escondido al oeste y otra en lo alto de la torre de entrenamiento. Pulsa E para cogerla y E junto al generador para insertarla.',
        spawnIds: ['cell-a', 'cell-b', 'cell-c'],
        targetId: 'socket',
      },
      {
        type: 'simultaneous',
        id: 'plates',
        title: 'Pisad las dos placas a la vez',
        hint: 'Mantened pulsadas las placas este y oeste de la plaza durante 3 segundos. ¿Estás solo? Empuja una caja encima de una placa.',
        plateIds: ['plate-west', 'plate-east'],
        holdSeconds: 3,
      },
      {
        type: 'codeMatch',
        id: 'calibrate',
        title: 'Calibra el generador',
        hint: 'La pantalla del parque muestra 3 colores. Gira las válvulas del generador (E) hasta que coincidan. ¡Comunicaos!',
        displayId: 'display',
        valveIds: ['valve-1', 'valve-2', 'valve-3'],
        colorCount: 4,
      },
      {
        type: 'holdZone',
        id: 'start',
        title: '¡Arrancad el generador!',
        hint: 'Tira de la palanca y quedaos dentro del anillo del generador mientras se carga.',
        triggerId: 'lever',
        zoneId: 'gen-ring',
        holdSeconds: 6,
        fraction: 0.5,
      },
    ],
    rewards: { successScore: 250, failScore: 50 },
    randomEvents: { pool: ['blackout', 'wind', 'lowGravity', 'confetti'], minIntervalSec: 45, maxIntervalSec: 75, durationSec: 9 },
    recovery: { itemRespawnSec: 45, dropOnDisconnect: true },
  },
};

export const VALVE_COLORS = [0xff4d6d, 0x4cc9f0, 0xffd60a, 0x80ed99] as const;
export const VALVE_COLOR_NAMES = ['Rojo', 'Azul', 'Amarillo', 'Verde'] as const;

export const RANDOM_EVENT_INFO: Record<RandomEventKind, { name: string; description: string; icon: string }> = {
  blackout: { name: 'Apagón', description: '¡Se va la luz! Buscad las farolas.', icon: '🌑' },
  wind: { name: 'Ventolera', description: 'Un viento huracanado empuja a todo el mundo.', icon: '🌪️' },
  lowGravity: { name: 'Gravedad loca', description: 'La gravedad se ha ido a tomar un café.', icon: '🪐' },
  confetti: { name: 'Fiesta sorpresa', description: 'Llueve confeti. No sirve para nada. ¡Disfrutad!', icon: '🎊' },
};

export const RED_BUTTON_EVENTS = ['lowGravity', 'wind', 'confetti', 'alarm', 'teleportAll', 'bigheadAll'] as const;
export type RedButtonEvent = (typeof RED_BUTTON_EVENTS)[number];
